import { Upload, Paperclip } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  SUPPORTED_ATTACHMENT_MIME_TYPES,
  buildFileAcceptString,
} from "@f2e/shared/constants"
import { Button } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { useDragAndDrop } from "@/hooks"
import { useToast } from "@/hooks"
import { filterSupportedFiles } from "@/lib/fileUtils"

// Pre-built accept string for file input (includes .md extension workaround)
const FILE_ACCEPT_STRING = buildFileAcceptString(
  SUPPORTED_ATTACHMENT_MIME_TYPES
)

interface InlineFileUploadProps {
  onFilesSelected: (files: File[]) => Promise<void>
  className?: string
  disabled?: boolean
}

export const InlineFileUpload = ({
  onFilesSelected,
  className,
  disabled = false,
}: InlineFileUploadProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()

  // Helper function to process files and handle unsupported types
  const processFiles = async (files: File[]) => {
    if (disabled) return

    // Filter files into supported and unsupported
    const { supportedFiles, unsupportedFiles } = filterSupportedFiles(
      files,
      SUPPORTED_ATTACHMENT_MIME_TYPES
    )

    // Show error notification for unsupported files
    if (unsupportedFiles.length > 0) {
      alertToast({
        title: t("common.fileUpload.error.title"),
        description: t("common.fileUpload.error.unsupportedType", {
          files: unsupportedFiles.map((f) => f.name).join(", "),
          defaultValue: "Unsupported file types: {{files}}",
        }),
      })
    }

    // Process supported files if there are any
    if (supportedFiles.length > 0) {
      await onFilesSelected(supportedFiles)
    }
  }

  // Use the custom drag and drop hook
  const { isDragging, dragHandlers } = useDragAndDrop({
    onFilesDropped: processFiles,
    logPrefix: "InlineFileUpload",
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return

    const files = Array.from(e.target.files || [])
    console.log(
      "InlineFileUpload - Selected files:",
      files.map((f) => f.name)
    )

    await processFiles(files)
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div
        className={cn(
          "flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors",
          isDragging
            ? "border-primary bg-primary/10"
            : "border-muted-foreground/25",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
        {...(disabled ? {} : dragHandlers)}
        data-testid="inline-file-upload-dropzone"
      >
        <Upload
          className={cn(
            "h-8 w-8 mb-3",
            isDragging ? "text-primary" : "text-muted-foreground"
          )}
        />
        <p className="text-sm font-medium mb-1">
          {t("common.fileUpload.dragAndDrop")}
        </p>
        <p className="text-xs text-muted-foreground mb-3 text-center">
          {t("common.fileUpload.dragAndDropDescription")}
        </p>
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="relative"
          >
            <Paperclip className="w-4 h-4 mr-2" />
            {t("common.fileUpload.selectFiles")}
            {!disabled && (
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
                accept={FILE_ACCEPT_STRING}
                aria-label={t("common.fileUpload.selectFiles")}
              />
            )}
          </Button>
        </div>
      </div>

      {/* File type support info */}
      <div className="text-xs text-muted-foreground">
        <p>{t("common.fileUpload.supportedFormats")}</p>
      </div>
    </div>
  )
}

export default InlineFileUpload
